/**
 * ExpandedTaskListModal - Full task list modal with filters from widget config
 */

import React, { useMemo, useState } from 'react';
import {
  CheckCircle2,
  Circle,
  Clock,
  Pause,
  AlertCircle,
  XCircle,
  Tag,
  Search,
} from 'lucide-react';
import { AdaptiveModal } from '../AdaptiveModal';
import type { TaskListWidgetConfig } from '../../types/widgets';
import { useTodos } from '../../hooks/useTodos';
import { groupTodos } from '../../utils/taskGrouping';
import { TaskStatus } from '../../types';
import type { Todo } from '../../types';
import { executeQuery } from '../../utils/queryEngine';
import { buildQueryFromUI } from '../../utils/queryBuilder';

interface ExpandedTaskListModalProps {
  config: TaskListWidgetConfig;
  isOpen: boolean;
  onClose: () => void;
}

const STATUS_ICONS: Record<string, React.ElementType> = {
  [TaskStatus.NOT_STARTED]: Circle,
  [TaskStatus.IN_PROGRESS]: Clock,
  [TaskStatus.COMPLETED]: CheckCircle2,
  [TaskStatus.WAITING]: Pause,
  [TaskStatus.CANCELED]: XCircle,
};

const STATUS_COLORS: Record<string, string> = {
  [TaskStatus.NOT_STARTED]: 'text-gray-400',
  [TaskStatus.IN_PROGRESS]: 'text-blue-500',
  [TaskStatus.COMPLETED]: 'text-green-500',
  [TaskStatus.WAITING]: 'text-yellow-500',
  [TaskStatus.CANCELED]: 'text-red-400',
};

const HORIZON_COLORS: Record<string, string> = {
  now: 'text-red-600 bg-red-50 dark:bg-red-900/30',
  next: 'text-orange-600 bg-orange-50 dark:bg-orange-900/30',
  soon: 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/30',
  later: 'text-blue-600 bg-blue-50 dark:bg-blue-900/30',
  someday: 'text-gray-500 bg-gray-50 dark:bg-gray-700',
};

function isOverdue(todo: Todo): boolean {
  if (!todo.deadline || todo.status === TaskStatus.COMPLETED || todo.status === TaskStatus.CANCELED) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(todo.deadline) < today;
}

export function ExpandedTaskListModal({ config, isOpen, onClose }: ExpandedTaskListModalProps) {
  const { todos, updateTodo } = useTodos();
  const [searchText, setSearchText] = useState('');

  const filteredTodos = useMemo(() => {
    let query = null;

    if (config.queryMode === 'advanced' && config.query) {
      query = config.query;
    } else {
      query = buildQueryFromUI({
        statusFilter: config.statusFilter,
        horizonFilter: config.horizonFilter,
        tagsFilter: config.tagsFilter,
        dateFilter: config.dateFilter,
        dateFilters: config.dateFilters,
        showCompleted: config.showCompleted,
        onlyWithDeadline: config.onlyWithDeadline,
        onlyOverdue: config.onlyOverdue,
      });
    }

    let result = executeQuery(todos, query);

    // Apply search filter
    if (searchText.trim()) {
      const search = searchText.toLowerCase();
      result = result.filter(
        (t) =>
          t.title.toLowerCase().includes(search) ||
          t.description?.toLowerCase().includes(search)
      );
    }

    // Apply sorting
    const sortDir = config.sortDirection === 'desc' ? -1 : 1;
    switch (config.sortBy) {
      case 'horizon': {
        const horizonOrder: Record<string, number> = { now: 0, next: 1, soon: 2, later: 3, someday: 4 };
        result.sort(
          (a, b) =>
            sortDir * ((horizonOrder[a.horizon ?? ''] ?? 5) - (horizonOrder[b.horizon ?? ''] ?? 5))
        );
        break;
      }
      case 'date':
        result.sort((a, b) => {
          const dateA = a.deadline || a.created_time;
          const dateB = b.deadline || b.created_time;
          return sortDir * (new Date(dateA).getTime() - new Date(dateB).getTime());
        });
        break;
      case 'deadline':
        result.sort((a, b) => {
          if (!a.deadline && !b.deadline) return 0;
          if (!a.deadline) return 1;
          if (!b.deadline) return -1;
          return sortDir * (new Date(a.deadline).getTime() - new Date(b.deadline).getTime());
        });
        break;
      case 'created':
        result.sort(
          (a, b) =>
            sortDir * (new Date(b.created_time).getTime() - new Date(a.created_time).getTime())
        );
        break;
      case 'title':
        result.sort((a, b) => sortDir * a.title.localeCompare(b.title));
        break;
    }

    return result;
  }, [todos, config, searchText]);

  const toggleStatus = (todo: Todo) => {
    const newStatus =
      todo.status === TaskStatus.COMPLETED ? TaskStatus.NOT_STARTED : TaskStatus.COMPLETED;
    updateTodo({ id: todo.id, data: { status: newStatus } });
  };

  const handleTaskClick = (todo: Todo) => {
    window.dispatchEvent(new CustomEvent('openTaskFromWidget', { detail: { todo } }));
  };

  const title = config.title || 'Tasks';

  // Group tasks
  const grouped = useMemo(
    () => groupTodos(filteredTodos, config.groupBy),
    [filteredTodos, config.groupBy]
  );
  const isGrouped = config.groupBy && config.groupBy !== 'none';

  // Build active filter summary
  const filterParts: string[] = [];
  if (config.statusFilter?.length) filterParts.push(`Status: ${config.statusFilter.join(', ')}`);
  if (config.tagsFilter?.length) filterParts.push(`Tags: ${config.tagsFilter.join(', ')}`);
  if (config.onlyOverdue) filterParts.push('Overdue only');
  if (config.onlyWithDeadline) filterParts.push('With deadline');

  const renderTask = (todo: Todo) => {
    const StatusIcon = STATUS_ICONS[todo.status] || Circle;
    const statusColor = STATUS_COLORS[todo.status] || 'text-gray-400';
    const overdueFlag = isOverdue(todo);

    return (
      <li
        key={todo.id}
        onClick={() => handleTaskClick(todo)}
        className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggleStatus(todo);
          }}
          className={`mt-0.5 flex-shrink-0 ${statusColor} hover:text-green-600 transition-colors`}
        >
          <StatusIcon className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p
              className={`text-sm ${
                todo.status === TaskStatus.COMPLETED || todo.status === TaskStatus.CANCELED
                  ? 'text-gray-400 line-through'
                  : 'text-gray-800 dark:text-gray-200'
              }`}
            >
              {todo.title}
            </p>
            {overdueFlag && (
              <AlertCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
            )}
          </div>
          {todo.description && (
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
              {todo.description}
            </p>
          )}
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {todo.deadline && (
              <span
                className={`flex items-center gap-0.5 text-xs ${
                  overdueFlag ? 'text-red-500' : 'text-gray-400'
                }`}
              >
                <AlertCircle className="w-3 h-3" />
                Due: {new Date(todo.deadline).toLocaleDateString()}
              </span>
            )}
            {todo.tags && todo.tags.length > 0 && (
              <span className="flex items-center gap-0.5 text-xs text-gray-400">
                <Tag className="w-3 h-3" />
                {todo.tags.join(', ')}
              </span>
            )}
          </div>
        </div>
        {todo.horizon && (
          <span
            className={`flex-shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded mt-1 ${HORIZON_COLORS[todo.horizon] || 'text-gray-400 bg-gray-50 dark:bg-gray-700'}`}
            title={`${todo.horizon} horizon`}
          >
            {todo.horizon}
          </span>
        )}
      </li>
    );
  };

  return (
    <AdaptiveModal isOpen={isOpen} onClose={onClose} title={`${title} (${filteredTodos.length})`} size="xl">
      {/* Search bar */}
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Search tasks..."
          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        />
      </div>

      {/* Active filter summary */}
      {filterParts.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {filterParts.map((part) => (
            <span
              key={part}
              className="px-2 py-0.5 text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded"
            >
              {part}
            </span>
          ))}
        </div>
      )}

      {/* Task list */}
      <div className="max-h-[60vh] overflow-auto">
        {filteredTodos.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <p className="text-sm">No tasks match filters</p>
          </div>
        ) : isGrouped ? (
          <div className="space-y-4">
            {grouped.map((group) => (
              <div key={group.key}>
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide px-2 mb-1 sticky top-0 bg-white dark:bg-gray-800 py-1">
                  {group.label} ({group.todos.length})
                </h4>
                <ul className="space-y-1">
                  {group.todos.map(renderTask)}
                </ul>
              </div>
            ))}
          </div>
        ) : (
          <ul className="space-y-1">
            {filteredTodos.map(renderTask)}
          </ul>
        )}
      </div>
    </AdaptiveModal>
  );
}
